<!--
  Invite landing page — where the "Accept your invite" email button lands
  (/invite/{code}). Public: the recipient usually has no account yet.

  It only LOOKS UP the invite (GET /api/public/invites/{code}) and shows
  who's inviting them; the actual redemption is server-side in
  AuthController::handleCallback(), reached by sending the user through
  the normal OIDC flow with ?invite={code}. That ordering matters — the
  callback accepts the invite BEFORE its entitlement gate, so a brand-new
  invitee's very first sign-in resolves instead of hitting Access denied.
-->
<script setup>
import axios from 'axios'
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const code = String(route.params.code ?? '')

const loading = ref(true)
const invite = ref(null)
const reason = ref('')

const REASON_TEXT = {
  'not_found': 'This invitation link isn\'t valid. Check that the full link was copied, or ask for a new invite.',
  expired: 'This invitation has expired. Ask your administrator to send a new one.',
  inactive: 'This invitation has been deactivated. Ask your administrator to send a new one.',
  unavailable: 'We couldn\'t check this invitation right now. Please try again in a moment.',
}

async function fetchInvite() {
  loading.value = true
  try {
    const { data } = await axios.get(`/api/public/invites/${encodeURIComponent(code)}`)

    if (data.valid) invite.value = data
    else reason.value = data.reason ?? 'not_found'
  } catch (err) {
    reason.value = err.response?.data?.reason ?? (err.response?.status === 404 ? 'not_found' : 'unavailable')
  } finally {
    loading.value = false
  }
}

onMounted(fetchInvite)

// Full-page navigation (not router push) — /api/auth/redirect is a server
// route that 302s to Persona (their site). login_hint pre-fills the
// invited email; register=1 lands on Persona's hosted Registration page
// (account creation lives on THEIR site), which returns into the OIDC
// flow — and our callback redeems the invite either way.
function accept(register = false) {
  const params = new URLSearchParams({ invite: code })

  if (invite.value?.email) params.set('login_hint', invite.value.email)
  if (register) params.set('register', '1')
  window.location.href = `/api/auth/redirect?${params.toString()}`
}
</script>

<template>
  <div class="invite-landing d-flex align-center justify-center">
    <VCard
      class="invite-card pa-2"
      max-width="440"
      width="100%"
    >
      <VCardText class="text-center">
        <div class="brand text-h6 font-weight-bold mb-6">
          Eat<span class="text-primary">Suite</span>
        </div>

        <template v-if="loading">
          <VProgressCircular
            indeterminate
            color="primary"
            size="40"
            class="mb-4"
          />
          <p class="text-body-2 text-medium-emphasis">
            Checking your invitation…
          </p>
        </template>

        <template v-else-if="invite">
          <VAvatar
            size="64"
            color="primary"
            variant="tonal"
            class="mb-4"
          >
            <VIcon
              icon="tabler-mail-opened"
              size="32"
            />
          </VAvatar>

          <h1 class="text-h5 mb-2">
            You're invited
          </h1>
          <p class="text-body-1 mb-1">
            You've been invited to join
            <strong>{{ invite.org_name || 'an organization' }}</strong> on EatSuite.
          </p>
          <p
            v-if="invite.email"
            class="text-body-2 text-medium-emphasis mb-4"
          >
            This invitation was sent to <strong>{{ invite.email }}</strong>.
          </p>

          <VBtn
            color="primary"
            size="large"
            block
            class="mb-3"
            @click="accept(true)"
          >
            Create your account
          </VBtn>
          <VBtn
            variant="tonal"
            color="primary"
            size="large"
            block
            class="mb-3"
            @click="accept(false)"
          >
            I already have an account — sign in
          </VBtn>
          <p class="text-caption text-medium-emphasis mb-0">
            Accounts are created and signed in securely on RightBytes. Your
            invitation is applied automatically either way.
          </p>
        </template>

        <template v-else>
          <VAvatar
            size="64"
            color="warning"
            variant="tonal"
            class="mb-4"
          >
            <VIcon
              icon="tabler-mail-x"
              size="32"
            />
          </VAvatar>

          <h1 class="text-h5 mb-2">
            Invitation unavailable
          </h1>
          <p class="text-body-2 text-medium-emphasis mb-4">
            {{ REASON_TEXT[reason] ?? REASON_TEXT['not_found'] }}
          </p>

          <VBtn
            v-if="reason === 'unavailable'"
            variant="tonal"
            color="primary"
            class="mb-3"
            @click="fetchInvite"
          >
            Try again
          </VBtn>

          <VBtn
            variant="text"
            :to="{ name: 'login' }"
          >
            Go to sign in
          </VBtn>
        </template>
      </VCardText>
    </VCard>
  </div>
</template>

<style scoped>
.invite-landing {
  min-block-size: 100vh;
  padding: 24px;
  background: rgb(var(--v-theme-background));
}

.invite-card {
  border-radius: 16px;
}
</style>
